import React, { useCallback, useRef, useState } from "react";

interface UploadFileProps {
  handleUpload: (file: File) => void;
  maxSizeBytes?: number;
  fileTypes: string[];
}

const prettyBytes = (bytes: number) => {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB", "TB"] as const;
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
};

const UploadFile: React.FC<UploadFileProps> = ({
  handleUpload,
  maxSizeBytes = 25 * 1024 * 1024,
  fileTypes,
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setDragging] = useState(false);
  const [isUploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [displayName, setDisplayName] = useState<string | null>(null);


  const selectFile = useCallback((f: File | null) => {
    setError(null);
    if (!f) {
      setFile(null);
      return;
    }
    // Basic validation: extension and size
    const name = f.name.toLowerCase();
    const isCsv = fileTypes.some((type: string) => name.endsWith(type) || f.type === type);
    if (!isCsv) {
      setError(`Please select a ${fileTypes.join(", ")} file.`);
      setFile(null);
      return;
    }
    if (f.size > maxSizeBytes) {
      setError(`File too large. Max ${prettyBytes(maxSizeBytes)}.`);
      setFile(null);
      return;
    }
    setFile(f);
    console.log('selectFile', f);
    // Don't auto-upload, let user control it
  }, [maxSizeBytes]);

  const onInputChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const f = e.target.files?.[0] ?? null;
    selectFile(f);
  };

  const onDrop: React.DragEventHandler<HTMLDivElement> = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
    const f = e.dataTransfer.files?.[0] ?? null;
    selectFile(f);
  };

  const onDragOver: React.DragEventHandler<HTMLDivElement> = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(true);
  };

  const onDragLeave: React.DragEventHandler<HTMLDivElement> = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
  };


  return (
    <div className="w-full bg-white rounded-lg p-4 shadow-sm">
      <div
        className={`relative rounded-2xl border-2 border-dashed p-6 transition-colors ${
          isDragging ? "border-blue-500 bg-blue-50" : "border-gray-300"
        }`}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
      >
        <input
          ref={inputRef}
          type="file"
          //   accept=".csv,text/csv"
          onChange={onInputChange}
          className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
          aria-label="Upload CSV"
        />
        <div className="pointer-events-none flex flex-col items-center justify-center text-center">
          <svg
            className="mb-3 h-10 w-10"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
          >
            <path
              strokeWidth="1.5"
              d="M12 16V4m0 12l-3-3m3 3l3-3M3 16v2a2 2 0 002 2h14a2 2 0 002-2v-2"
            />
          </svg>
          <p className="text-sm text-gray-700">
            <strong>
              Click to upload
            </strong>
            {' '}
            or drag & drop a
            {' '}
            {fileTypes.join(", ")}
            {' '}
            file
          </p>
          <p className="text-xs text-gray-500">
            Only
            {' '}
            {fileTypes.join(", ")}
            {' '}
            files are supported
          </p>
        </div>
      </div>

      {file && (
        <div className="mt-3 flex items-center justify-between rounded-lg p-3 bg-gray-200">

          <div className="min-w-0">
            <p className="truncate text-sm font-medium">
              {file.name}
            </p>
            <p className="text-xs text-gray-500">
              {prettyBytes(file.size)}
            </p>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => {
                setFile(null);
                setDisplayName(null);
                if (inputRef.current) inputRef.current.value = "";
              }}
              className="rounded-md border px-3 py-1 text-sm"
              disabled={isUploading}
            >
              Clear
            </button>
            <button
              type="button"
              onClick={() => handleUpload(file)}
              className="rounded-md bg-blue-600 px-3 py-1 text-sm text-white disabled:opacity-50"
              disabled={isUploading}
            >
              {isUploading ? "Uploading…" : "Upload"}
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="mt-3 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      )}
    </div>
  );
};

export default UploadFile;
